import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { UserCircle, KeyRound, Mail, ArrowLeft, Bell, BellOff } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

export default function Profil() {
  const { user, setUser } = useAuth();
  const [form, setForm] = useState({ name: user?.name || "", email: user?.email || "" });
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [emailNotifs, setEmailNotifs] = useState(user?.email_notifications !== false);
  const [savingNotifs, setSavingNotifs] = useState(false);
  const showEmailToggle = user?.role === "auftraggeber";

  const saveProfile = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.patch("/auth/me", { email: form.email, name: form.name });
      if (data.user) setUser({ ...user, email: data.user.email, name: data.user.name });
      toast.success("Profil aktualisiert");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Speichern fehlgeschlagen");
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (pw.next !== pw.confirm) {
      toast.error("Passwörter stimmen nicht überein");
      return;
    }
    try {
      await api.patch("/auth/me", { password: pw.next });
      toast.success("Passwort geändert");
      setPw({ current: "", next: "", confirm: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Ändern fehlgeschlagen");
    }
  };

  const toggleEmailNotifs = async (next) => {
    setSavingNotifs(true);
    try {
      const { data } = await api.patch("/auth/me", { email_notifications: next });
      setEmailNotifs(next);
      if (data.user) setUser({ ...user, email_notifications: data.user.email_notifications });
      toast.success(next ? "E-Mail-Benachrichtigungen aktiviert" : "E-Mail-Benachrichtigungen deaktiviert");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Speichern fehlgeschlagen");
    } finally {
      setSavingNotifs(false);
    }
  };

  return (
    <div className="p-4 lg:p-8 space-y-6 animate-fade-up max-w-3xl" data-testid="profil-page">
      <div>
        <Link to="/einstellungen" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mb-2">
          <ArrowLeft className="h-3 w-3" /> Zurück zu Einstellungen
        </Link>
        <div className="overline">Persönlich</div>
        <h1 className="font-heading text-3xl lg:text-4xl font-bold tracking-tight mt-2">Mein Profil</h1>
        <p className="text-muted-foreground mt-1">Deinen Login-Zugang selbst verwalten.</p>
      </div>

      <Card className="border-border">
        <CardContent className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <UserCircle className="h-4 w-4 text-primary" />
            <div className="text-sm font-medium">Kontodaten</div>
          </div>
          <form onSubmit={saveProfile} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="p-name">Name</Label>
              <Input id="p-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="p-name" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="p-email">E-Mail</Label>
              <Input id="p-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="p-email" />
            </div>
            <div className="pt-2">
              <Button type="submit" data-testid="p-save">Speichern</Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <Card className="border-border">
        <CardContent className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <KeyRound className="h-4 w-4 text-primary" />
            <div className="text-sm font-medium">Passwort ändern</div>
          </div>
          <form onSubmit={changePassword} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="p-pw">Neues Passwort (mind. 6 Zeichen)</Label>
              <Input id="p-pw" type="password" minLength={6} value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} required data-testid="p-pw-new" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="p-pw2">Passwort bestätigen</Label>
              <Input id="p-pw2" type="password" minLength={6} value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} required data-testid="p-pw-confirm" />
            </div>
            <div className="pt-2">
              <Button type="submit" data-testid="p-pw-submit">Passwort ändern</Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <Card className="border-border">
        <CardContent className="p-6 flex items-center gap-3">
          <Mail className="h-5 w-5 text-muted-foreground" />
          <div className="text-xs text-muted-foreground">
            Deine Rolle: <span className="font-medium text-foreground">{user?.role}</span>. Nach E-Mail-Änderung musst du dich mit der neuen Adresse einloggen.
          </div>
        </CardContent>
      </Card>

      {showEmailToggle && (
        <Card className="border-border" data-testid="notif-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-start gap-3 min-w-0">
                {emailNotifs ? <Bell className="h-5 w-5 text-primary mt-0.5" /> : <BellOff className="h-5 w-5 text-muted-foreground mt-0.5" />}
                <div className="min-w-0">
                  <div className="text-sm font-medium">E-Mail bei neuen Kunden-Nachrichten</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    Wenn ein Mitarbeiter dir eine Kunden-Nachricht schreibt, bekommst du eine E-Mail. In der App bist du weiterhin über den roten Zähler informiert.
                  </div>
                </div>
              </div>
              <Button
                variant={emailNotifs ? "default" : "outline"}
                size="sm"
                disabled={savingNotifs}
                onClick={() => toggleEmailNotifs(!emailNotifs)}
                data-testid="notif-toggle"
                className="shrink-0"
              >
                {emailNotifs ? "Aktiv" : "Aus"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
